import { useEffect } from 'react';
import { Toaster as Sonner } from 'sonner';
import { Bell, Check, X } from 'lucide-react';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { decorateToastsInDom } from '@/lib/toast/toastTracking';

// Glyphs only; the coloured circle behind each is drawn in index.css.
const toastIcons = {
    success: <Check strokeWidth={3} aria-hidden="true" />,
    error: <X strokeWidth={3} aria-hidden="true" />,
    warning: <span className="toast-glyph-bang" aria-hidden="true">!</span>,
    info: <Bell aria-hidden="true" />,
};

// Below the `sm` breakpoint toasts center instead of sitting top-right.
const TOAST_DESKTOP_BREAKPOINT = 640;

// Top offset clears the sticky <Menu> header (80px at lg, else 64px) plus a gap.
const TOAST_GAP = '8px';
const TOAST_TOP_LG = '88px';
const TOAST_TOP_BELOW_LG = '72px';

// sonner's full-width mobile layout ignores `offset`, so mirror the top here.
const TOAST_MOBILE_OFFSET = { top: TOAST_TOP_BELOW_LG, left: '16px', right: '16px' };

const Toaster = ({ ...props }: React.ComponentProps<typeof Sonner>) => {
    const isDesktop = useIsDesktop(TOAST_DESKTOP_BREAKPOINT);
    const isLgUp = useIsDesktop(1024);

    useEffect(() => {
        const root = document.querySelector('[data-sonner-toaster]');
        if (!root) return;

        const observer = new MutationObserver(() => decorateToastsInDom());
        observer.observe(root, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    return (
        <Sonner
            className="toaster group"
            position={isDesktop ? 'top-right' : 'top-center'}
            offset={{ top: isLgUp ? TOAST_TOP_LG : TOAST_TOP_BELOW_LG, right: TOAST_GAP }}
            mobileOffset={TOAST_MOBILE_OFFSET}
            visibleToasts={4}
            closeButton
            icons={toastIcons}
            toastOptions={{
                duration: 4000,
                // Style everything from index.css; sonner's unlayered defaults
                // would otherwise win the cascade against our @layer styles.
                unstyled: true,
                classNames: {
                    toast: 'gz-toast',
                    title: 'gz-toast-title',
                    description: 'gz-toast-description',
                    icon: 'gz-toast-icon',
                    closeButton: 'gz-toast-close',
                    actionButton: 'gz-toast-action',
                },
            }}
            {...props}
        />
    );
};

export { Toaster };
